import React, { useCallback } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';
import { CollapsibleListSectionCollapsedContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content/collapsed-content';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';
import { CollapsibleListSectionSubHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-sub-header';

import { usePurchaseOrderExpanderSection } from '../purchase-order-expandable-section-provider';
import style from './expandable-section.module.less';

/**
 * @module ExpandableSection
 * @description A reusable component for creating expandable/collapsible sections in the purchase order UI.
 * This component uses the PurchaseOrderExpanderSection context to manage expansion state.
 * @param {object} props - Component props
 * @param {string} props.sectionId - Unique identifier for the section
 * @param {React.ReactNode} props.headerContent - Content to display in the header
 * @param {React.ReactNode} props.sectionContent - Content to display when expanded
 * @param {React.ReactNode} props.collapsedSectionContent - Content to display when collapsed (optional)
 * @param {string} props.className - Additional class name for the section
 * @param {string} props.headerClassName - Additional class name for the header
 * @param {string} props.contentClassName - Additional class name for the content
 * @param {string} props.collapsedContentClassName - Additional class name for the collapsed content
 * @param {string} props.pendoTrackingId - ID for Pendo tracking
 * @param {React.ReactNode} props.subHeaderContent - Content to display in the sub-header (optional)
 * @param {string} props.subHeaderClassName - Additional class name for the sub-header
 * @param {object} props.ariaLabelMessageDescriptor - Message descriptor for aria-label
 * @param {boolean} props.isFullHeightWhenExpanded - Whether the section should take full height when expanded
 * @returns {JSX.Element} The expandable section component
 */
export const ExpandableSection = ({
  sectionId,
  headerContent,
  sectionContent,
  collapsedSectionContent,
  className,
  headerClassName,
  contentClassName,
  collapsedContentClassName,
  pendoTrackingId,
  subHeaderContent,
  subHeaderClassName,
  ariaLabelMessageDescriptor,
  isFullHeightWhenExpanded = false,
}) => {
  const { isExpanded, toggleIsExpanded } = usePurchaseOrderExpanderSection(sectionId);
  const { formatMessage } = useIntl();
  const isFullHeightClassName = isFullHeightWhenExpanded && isExpanded ? style['full-height'] : undefined;

  const onSectionHeaderClick = useCallback(
    (e) => {
      if (!e?.defaultPrevented) {
        toggleIsExpanded();
      }
    },
    [toggleIsExpanded],
  );

  return (
    <section aria-label={formatMessage(ariaLabelMessageDescriptor)} className={isFullHeightClassName}>
      <CollapsibleListSection className={classnames(isFullHeightClassName, className)} open={isExpanded}>
        <CollapsibleListSectionHeader
          className={classnames(style.header, headerClassName)}
          contentClassName={style['header-content']}
          onClick={onSectionHeaderClick}
          pendoTrackingId={pendoTrackingId}
          theme='white'
        >
          {headerContent}
        </CollapsibleListSectionHeader>
        {subHeaderContent ? (
          <CollapsibleListSectionSubHeader className={classnames(style['sub-header'], subHeaderClassName)}>
            {subHeaderContent}
          </CollapsibleListSectionSubHeader>
        ) : (
          <React.Fragment />
        )}
        <CollapsibleListSectionContent className={classnames(style.content, contentClassName)}>
          {sectionContent}
        </CollapsibleListSectionContent>
        {collapsedSectionContent ? (
          <CollapsibleListSectionCollapsedContent className={classnames(style.content, collapsedContentClassName)}>
            {collapsedSectionContent}
          </CollapsibleListSectionCollapsedContent>
        ) : (
          <React.Fragment />
        )}
      </CollapsibleListSection>
    </section>
  );
};
